import React, { useState, useEffect, useCallback } from "react";
import { GetApiLogs, GetApiLogById } from "../apis/get";
import LoaderService from "../../services/LoaderService";
import toast from "react-hot-toast";
import {
  FileText,
  User,
  Activity,
  Clock,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Globe,
  Code2,
  CheckCircle2,
  XCircle,
  X,
  Eye,
  Info,
  Filter
} from "lucide-react";
import { createPortal } from "react-dom";

const LogDetailModal = ({ logId, onClose }) => {
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("request");

  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const response = await GetApiLogById(logId);
        if (response) {
          setLog(response);
        } else {
          toast.error("Failed to fetch log details");
          onClose();
        }
      } catch (err) {
        console.error("Failed to load api log detail:", err);
        toast.error("Failed to load log details");
        onClose();
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [logId, onClose]);

  const formatPayload = (payload) => {
    if (!payload) return "null";
    if (typeof payload === "object") {
      return JSON.stringify(payload, null, 2);
    }
    try {
      const parsed = JSON.parse(payload);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      return String(payload);
    }
  };

  if (loading) {
    return createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 flex flex-col items-center gap-4 shadow-2xl">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
          <span className="text-xs font-black text-slate-400 uppercase tracking-widest">Loading Details...</span>
        </div>
      </div>,
      document.body
    );
  }

  if (!log) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl animate-in zoom-in-95 duration-200">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/50">
          <div className="flex flex-col gap-1">
            <h3 className="text-sm font-black text-white uppercase tracking-wider italic flex items-center gap-2">
              <Code2 className="w-4 h-4 text-blue-500" />
              API Log Details
            </h3>
            <span className="text-xs text-slate-500 font-mono">Log ID: #{log.id}</span>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-800 rounded-xl text-slate-400 hover:text-white transition-colors"
          >
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto custom-scrollbar flex-1 space-y-5 bg-slate-950/30">
          {/* Endpoint Block */}
          <div className="bg-slate-950/80 p-4 rounded-xl border border-slate-800/80 space-y-1.5">
            <label className="text-xs font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5 text-blue-500" />
              Endpoint URL
            </label>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-black px-2.5 py-0.5 rounded border ${
                log.method === "GET" ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/20" :
                log.method === "POST" ? "text-blue-400 bg-blue-500/10 border-blue-500/20" :
                log.method === "PUT" ? "text-indigo-400 bg-indigo-500/10 border-indigo-500/20" :
                log.method === "DELETE" ? "text-red-400 bg-red-500/10 border-red-500/20" :
                "text-slate-400 bg-slate-500/10 border-slate-500/20"
              }`}>
                {log.method}
              </span>
              <span className="text-sm font-mono text-slate-300 select-all break-all leading-relaxed">
                {log.endpoint || "N/A"}
              </span>
            </div>
          </div>

          {/* Grid Metadata */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-950/50 p-4 rounded-xl border border-slate-800/50 space-y-1">
              <span className="text-xs font-black text-slate-500 uppercase tracking-widest">User Details</span>
              <p className="text-sm text-slate-200 font-bold">
                ID: <span className="font-mono text-blue-400 select-all">{log.user_id || "null"}</span>
              </p>
              <p className="text-xs text-slate-400 font-bold mt-1">
                Type: <span className="uppercase tracking-wider px-1.5 py-0.5 rounded text-[10px] bg-slate-800 border border-slate-700 text-slate-300">{log.user_type}</span>
              </p>
            </div>
            <div className="bg-slate-950/50 p-4 rounded-xl border border-slate-800/50 space-y-1">
              <span className="text-xs font-black text-slate-500 uppercase tracking-widest">Metadata</span>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Status Code:</span>
                  <span className={`font-black font-mono ${log.http_status >= 200 && log.http_status < 300 ? "text-emerald-400" : "text-rose-400"}`}>{log.http_status}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Duration:</span>
                  <span className="font-mono text-slate-300">{parseFloat(log.duration_ms || 0).toFixed(2)} ms</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">IP Address:</span>
                  <span className="font-mono text-slate-300 select-all">{log.ip_address || "N/A"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* User Agent Block */}
          {log.user_agent && (
            <div className="bg-slate-950/50 p-4 rounded-xl border border-slate-800/50 space-y-1">
              <span className="text-xs font-black text-slate-500 uppercase tracking-widest">User Agent</span>
              <p className="text-sm text-slate-400 font-mono select-all break-words leading-relaxed">{log.user_agent}</p>
            </div>
          )}

          {/* Payload tabs */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 p-1 bg-slate-950 border border-slate-800/60 rounded-xl w-fit">
              <button
                onClick={() => setActiveTab("request")}
                className={`px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all ${
                  activeTab === "request"
                    ? "bg-blue-600 text-white"
                    : "text-slate-500 hover:text-slate-300"
                }`}
              >
                Request Payload
              </button>
              <button
                onClick={() => setActiveTab("response")}
                className={`px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all ${
                  activeTab === "response"
                    ? "bg-blue-600 text-white"
                    : "text-slate-500 hover:text-slate-300"
                }`}
              >
                Response Payload
              </button>
              <button
                onClick={() => setActiveTab("query")}
                className={`px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all ${
                  activeTab === "query"
                    ? "bg-blue-600 text-white"
                    : "text-slate-500 hover:text-slate-300"
                }`}
              >
                Query Params
              </button>
            </div>

            <pre className="text-sm font-mono text-blue-300 leading-relaxed whitespace-pre-wrap break-all bg-slate-950 p-4 rounded-xl border border-slate-800/50 max-h-[35vh] overflow-y-auto custom-scrollbar select-all">
              {activeTab === "request"
                ? formatPayload(log.request_payload)
                : activeTab === "response"
                ? formatPayload(log.response_payload)
                : formatPayload(log.query_params)}
            </pre>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-slate-800 flex justify-between items-center bg-slate-950/50">
          <span className="text-xs text-slate-500 font-mono">{new Date(log.created_at).toLocaleString()}</span>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-black uppercase tracking-widest rounded-xl transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default function ApiLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLogId, setSelectedLogId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isCustomStatus, setIsCustomStatus] = useState(false);

  const handleStatusSelectChange = (e) => {
    const val = e.target.value;
    if (val === "other") {
      setIsCustomStatus(true);
      handleTempFilterChange("status", "");
    } else {
      setIsCustomStatus(false);
      handleTempFilterChange("status", val);
    }
  };
  
  const [pagination, setPagination] = useState({
    offset: 0,
    limit: 100,
    total: 0
  });

  const [filters, setFilters] = useState({
    user_id: "",
    user_type: "",
    method: "",
    endpoint: "",
    status: "",
    start_date: "",
    end_date: ""
  });

  const [tempFilters, setTempFilters] = useState({
    user_id: "",
    user_type: "",
    method: "",
    endpoint: "",
    status: "",
    start_date: "",
    end_date: ""
  });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    LoaderService.show();
    try {
      const response = await GetApiLogs(pagination.offset, pagination.limit, filters);
      if (Array.isArray(response)) {
        setLogs(response);
        // Estimate total logs: if we got a full page, allow going next.
        // If the backend has a custom count payload we could map it, but it returns a List[ApiLogResponse] directly,
        // so response is a plain Array. Let's compute estimated total.
        setPagination(prev => ({
          ...prev,
          total: response.length < prev.limit ? prev.offset + response.length : prev.offset + prev.limit + 1
        }));
      } else {
        setLogs([]);
      }
    } catch (error) {
      console.error("Failed to load API logs:", error);
      toast.error("Failed to load API logs");
      setLogs([]);
    } finally {
      setLoading(false);
      LoaderService.hide();
    }
  }, [pagination.offset, pagination.limit, filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Debounced filters updates
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        user_id: tempFilters.user_id,
        user_type: tempFilters.user_type,
        method: tempFilters.method,
        endpoint: tempFilters.endpoint,
        status: tempFilters.status,
        start_date: tempFilters.start_date,
        end_date: tempFilters.end_date
      }));
      setPagination(prev => ({ ...prev, offset: 0 }));
    }, 500);

    return () => clearTimeout(timer);
  }, [
    tempFilters.user_id,
    tempFilters.user_type,
    tempFilters.method,
    tempFilters.endpoint,
    tempFilters.status,
    tempFilters.start_date,
    tempFilters.end_date
  ]);

  const handleTempFilterChange = (key, value) => {
    setTempFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setFilters(tempFilters);
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const handleResetFilters = () => {
    const cleared = {
      user_id: "",
      user_type: "",
      method: "",
      endpoint: "",
      status: "",
      start_date: "",
      end_date: ""
    };
    setTempFilters(cleared);
    setFilters(cleared);
    setPagination(prev => ({ ...prev, offset: 0 }));
    setIsCustomStatus(false);
  };

  const handlePrevPage = () => {
    if (pagination.offset >= pagination.limit) {
      setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }));
    }
  };

  const handleNextPage = () => {
    if (logs.length >= pagination.limit) {
      setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }));
    }
  };

  const getMethodBadgeClass = (method) => {
    switch (method?.toUpperCase()) {
      case "GET":
        return "text-emerald-400 bg-emerald-500/10 border-emerald-500/20";
      case "POST":
        return "text-blue-400 bg-blue-500/10 border-blue-500/20";
      case "PUT":
        return "text-indigo-400 bg-indigo-500/10 border-indigo-500/20";
      case "PATCH":
        return "text-amber-400 bg-amber-500/10 border-amber-500/20";
      case "DELETE":
        return "text-red-400 bg-red-500/10 border-red-500/20";
      default:
        return "text-slate-400 bg-slate-500/10 border-slate-500/20";
    }
  };

  const getStatusBadge = (status) => {
    const isSuccess = status >= 200 && status < 300;
    return (
      <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-base font-black uppercase tracking-wider ${
        isSuccess
          ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
          : "bg-red-500/10 text-red-400 border-red-500/20"
      }`}>
        {isSuccess ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
        {status}
      </div>
    );
  };

  const getDurationBadge = (durationMs) => {
    const duration = parseFloat(durationMs || 0);
    let colorClass = "text-emerald-400 bg-emerald-500/10 border-emerald-500/20";
    let speedLabel = "Fast";

    if (duration >= 1000) {
      colorClass = "text-red-400 bg-red-500/10 border-red-500/20";
      speedLabel = "Slow";
    } else if (duration >= 200) {
      colorClass = "text-amber-400 bg-amber-500/10 border-amber-500/20";
      speedLabel = "Normal";
    }

    return (
      <div className={`inline-flex flex-col gap-1 px-3 py-1 rounded-lg border text-base font-bold ${colorClass}`}>
        <span className="font-mono">{duration.toFixed(0)} ms</span>
        <span className="text-[12px] uppercase tracking-widest font-black text-center">{speedLabel}</span>
      </div>
    );
  };

  // Client-side search filters endpoint, user ID, or ID
  const filteredLogs = logs.filter((log) => {
    const term = searchTerm.toLowerCase();
    if (!term) return true;
    return (
      log.id?.toString().includes(term) ||
      log.user_id?.toLowerCase().includes(term) ||
      log.endpoint?.toLowerCase().includes(term)
    );
  });

  const totalPages = Math.ceil(pagination.total / pagination.limit) || 1;
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

  return (
    <div className="mt-12 space-y-6 animate-fade-in p-4 sm:p-6 lg:p-8">
      {/* Header section */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div className="relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
          <div className="relative flex items-center gap-4 bg-slate-950 px-6 py-3 rounded-xl border border-slate-800">
            <div className="p-2 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <Activity className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-white tracking-tight uppercase italic flex items-center gap-2">
                API Logs
              </h1>
              <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.2em] mt-0.5">
                HTTP Requests Audit & Payload Viewer
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
          {/* Client-side search bar */}
          <div className="relative w-full sm:w-64 group">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
              <Search className="w-4 h-4 text-slate-500 group-focus-within:text-blue-500 transition-colors" />
            </div>
            <input
              type="text"
              placeholder="Search in page..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-10 py-2.5 bg-slate-900/50 border border-slate-800 text-slate-200 text-base font-bold rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all placeholder:text-slate-600 placeholder:font-black placeholder:uppercase placeholder:tracking-widest"
            />
            {searchTerm && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="p-1 text-slate-500 hover:text-red-400 hover:bg-slate-800 rounded-lg transition-colors cursor-pointer"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>

          <button
            onClick={fetchLogs}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white rounded-xl border border-slate-800 transition-all active:scale-95 disabled:opacity-50 group whitespace-nowrap"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'}`} />
            <span className="text-base font-black uppercase tracking-wider">Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters (Server-side) */}
      <div className="bg-slate-900/40 border border-slate-800/60 rounded-[2rem] p-6 backdrop-blur-md shadow-2xl space-y-4">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
          <div className="flex items-center gap-2 text-white font-black uppercase tracking-wider text-base italic">
            <Filter className="w-5 h-5 text-blue-500" />
            Filter Request Logs
          </div>
          {(filters.user_id || filters.user_type || filters.method || filters.endpoint || filters.status || filters.start_date || filters.end_date) && (
            <span className="text-sm bg-blue-500/10 text-blue-400 border border-blue-500/20 px-2.5 py-0.5 rounded-full font-black uppercase tracking-wider">
              Filters Active
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {/* User ID */}
          <div className="flex flex-col gap-1.5">
            <label className="text-base font-black text-slate-500 uppercase tracking-widest">User ID</label>
            <input
              type="text"
              placeholder="e.g. U861781144545"
              value={tempFilters.user_id}
              onChange={(e) => handleTempFilterChange("user_id", e.target.value)}
              className="block w-full px-4 py-2.5 bg-slate-950 border border-slate-800/80 text-slate-200 text-lg font-bold rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
            />
          </div>

          {/* User Type */}
          <div className="flex flex-col gap-1.5">
            <label className="text-base font-black text-slate-500 uppercase tracking-widest">User Type</label>
            <select
              value={tempFilters.user_type}
              onChange={(e) => handleTempFilterChange("user_type", e.target.value)}
              className="block w-full px-4 py-2.5 bg-slate-950 border border-slate-800/80 text-slate-200 text-lg font-bold rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-slate-300"
            >
              <option value="" className="bg-slate-950">All Types</option>
              <option value="user" className="bg-slate-950">User</option>
              <option value="admin" className="bg-slate-950">Admin</option>
              <option value="anonymous" className="bg-slate-950">Anonymous</option>
            </select>
          </div>

          {/* HTTP Method */}
          <div className="flex flex-col gap-1.5">
            <label className="text-base font-black text-slate-500 uppercase tracking-widest">Method</label>
            <select
              value={tempFilters.method}
              onChange={(e) => handleTempFilterChange("method", e.target.value)}
              className="block w-full px-4 py-2.5 bg-slate-950 border border-slate-800/80 text-slate-200 text-lg font-bold rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-slate-300"
            >
              <option value="" className="bg-slate-950">All Methods</option>
              <option value="GET" className="bg-slate-950">GET</option>
              <option value="POST" className="bg-slate-950">POST</option>
              <option value="PUT" className="bg-slate-950">PUT</option>
              <option value="PATCH" className="bg-slate-950">PATCH</option>
              <option value="DELETE" className="bg-slate-950">DELETE</option>
            </select>
          </div>

          {/* Status */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <label className="text-base font-black text-slate-500 uppercase tracking-widest">HTTP Status</label>
              {isCustomStatus && (
                <button
                  type="button"
                  onClick={() => {
                    setIsCustomStatus(false);
                    handleTempFilterChange("status", "");
                  }}
                  className="text-xs font-bold text-blue-500 hover:text-blue-400 uppercase tracking-wider transition-colors"
                >
                  Select from List
                </button>
              )}
            </div>
            {!isCustomStatus ? (
              <select
                value={tempFilters.status}
                onChange={handleStatusSelectChange}
                className="block w-full px-4 py-2.5 bg-slate-950 border border-slate-800/80 text-slate-200 text-lg font-bold rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-slate-300"
              >
                <option value="" className="bg-slate-950">All Statuses</option>
                <option value="200" className="bg-slate-950">200 - OK</option>
                <option value="201" className="bg-slate-950">201 - Created</option>
                <option value="400" className="bg-slate-950">400 - Bad Request</option>
                <option value="401" className="bg-slate-950">401 - Unauthorized</option>
                <option value="404" className="bg-slate-950">404 - Not Found</option>
                <option value="500" className="bg-slate-950">500 - Internal Server Error</option>
                <option value="502" className="bg-slate-950">502 - Bad Gateway</option>
                <option value="other" className="bg-slate-950">Other...</option>
              </select>
            ) : (
              <input
                type="number"
                placeholder="e.g. 403"
                value={tempFilters.status}
                onChange={(e) => handleTempFilterChange("status", e.target.value)}
                autoFocus
                className="block w-full px-4 py-2.5 bg-slate-950 border border-slate-800/80 text-slate-200 text-lg font-bold rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
              />
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
          {/* Endpoint */}
          <div className="flex flex-col gap-1.5 sm:col-span-1">
            <label className="text-base font-black text-slate-500 uppercase tracking-widest">Endpoint Path</label>
            <input
              type="text"
              placeholder="e.g. /users"
              value={tempFilters.endpoint}
              onChange={(e) => handleTempFilterChange("endpoint", e.target.value)}
              className="block w-full px-4 py-2.5 bg-slate-950 border border-slate-800/80 text-slate-200 text-lg font-bold rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
            />
          </div>

          {/* Start Date */}
          <div className="flex flex-col gap-1.5">
            <label className="text-base font-black text-slate-500 uppercase tracking-widest">Start Date</label>
            <input
              type="datetime-local"
              value={tempFilters.start_date}
              onChange={(e) => handleTempFilterChange("start_date", e.target.value)}
              className="block w-full px-4 py-2.5 bg-slate-950 border border-slate-800/80 text-slate-200 text-lg font-bold rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-slate-300"
            />
          </div>

          {/* End Date */}
          <div className="flex flex-col gap-1.5">
            <label className="text-base font-black text-slate-500 uppercase tracking-widest">End Date</label>
            <input
              type="datetime-local"
              value={tempFilters.end_date}
              onChange={(e) => handleTempFilterChange("end_date", e.target.value)}
              className="block w-full px-4 py-2.5 bg-slate-950 border border-slate-800/80 text-slate-200 text-lg font-bold rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-slate-300"
            />
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-slate-800/60">
          <div className="flex items-center gap-2">
            <label className="text-base font-black text-slate-500 uppercase tracking-widest">Page Limit:</label>
            <select
              value={pagination.limit}
              onChange={(e) => {
                const newLimit = Number(e.target.value);
                setPagination(prev => ({ ...prev, limit: newLimit, offset: 0 }));
              }}
              className="px-2.5 py-1 bg-slate-950 border border-slate-800 text-slate-300 text-lg font-bold rounded-lg focus:ring-1 focus:ring-blue-500 outline-none"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleResetFilters}
              className="px-4 py-2 bg-slate-950 hover:bg-slate-800 text-slate-400 hover:text-white border border-slate-800/80 rounded-xl transition-all text-sm font-black uppercase tracking-wider"
            >
              Reset
            </button>
            <button
              onClick={handleApplyFilters}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl transition-all text-sm font-black uppercase tracking-wider shadow-lg shadow-blue-500/10 hover:shadow-blue-500/20 active:scale-95"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-slate-900/40 border border-slate-800/60 rounded-[2rem] overflow-hidden backdrop-blur-md shadow-2xl relative">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/[0.02] to-transparent pointer-events-none"></div>

        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse relative">
            <thead>
              <tr className="border-b border-slate-800/80 bg-slate-950/40">
                <th className="px-6 py-5 text-lg font-black text-slate-500 uppercase tracking-widest">ID</th>
                <th className="px-6 py-5 text-lg font-black text-slate-500 uppercase tracking-widest">Timestamp</th>
                <th className="px-6 py-5 text-lg font-black text-slate-500 uppercase tracking-widest">User ID & Type</th>
                <th className="px-6 py-5 text-lg font-black text-slate-500 uppercase tracking-widest">Method</th>
                <th className="px-6 py-5 text-lg font-black text-slate-500 uppercase tracking-widest">Endpoint</th>
                <th className="px-6 py-5 text-lg font-black text-slate-500 uppercase tracking-widest">Status</th>
                <th className="px-6 py-5 text-lg font-black text-slate-500 uppercase tracking-widest">Duration</th>
                <th className="px-6 py-5 text-lg font-black text-slate-500 uppercase tracking-widest text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {loading && filteredLogs.length === 0 ? (
                Array(5).fill(0).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td colSpan={8} className="px-6 py-5">
                      <div className="h-4 bg-slate-800 rounded w-full"></div>
                    </td>
                  </tr>
                ))
              ) : filteredLogs.length > 0 ? (
                filteredLogs.map((log) => (
                  <tr
                    key={log.id}
                    className="hover:bg-slate-800/30 transition-all duration-200 border-slate-800/30 group"
                  >
                    {/* ID */}
                    <td className="px-6 py-4 text-lg font-mono font-black text-slate-500">#{log.id}</td>

                    {/* Timestamp */}
                    <td className="px-6 py-4 text-lg text-slate-300 font-mono whitespace-nowrap">
                      {new Date(log.created_at).toLocaleString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false
                      })}
                    </td>

                    {/* User ID & Type */}
                    <td className="px-6 py-4 text-lg">
                      <div className="flex flex-col gap-1">
                        <span className="font-bold font-mono text-slate-200 select-all truncate max-w-[180px]" title={log.user_id}>
                          {log.user_id || "null"}
                        </span>
                        <span className={`text-xs font-black uppercase tracking-widest w-fit px-2 py-0.5 rounded border ${
                          log.user_type === "admin" ? "bg-purple-500/10 text-purple-400 border-purple-500/20" :
                          log.user_type === "user" ? "bg-sky-500/10 text-sky-400 border-sky-500/20" :
                          "bg-slate-500/10 text-slate-400 border-slate-500/20"
                        }`}>
                          {log.user_type}
                        </span>
                      </div>
                    </td>

                    {/* Method */}
                    <td className="px-6 py-4 text-lg">
                      <span className={`px-3 py-1.5 rounded-lg text-sm font-black border uppercase tracking-wider ${getMethodBadgeClass(log.method)}`}>
                        {log.method}
                      </span>
                    </td>

                    {/* Endpoint */}
                    <td className="px-6 py-4 text-lg font-mono text-slate-300 max-w-[300px] truncate" title={log.endpoint}>
                      {log.endpoint}
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4 text-lg font-bold">{getStatusBadge(log.http_status)}</td>

                    {/* Duration */}
                    <td className="px-6 py-4 text-lg">{getDurationBadge(log.duration_ms)}</td>

                    {/* Actions */}
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => setSelectedLogId(log.id)}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-slate-900 border border-slate-800 text-slate-300 hover:text-white rounded-lg hover:border-blue-500/30 hover:bg-blue-600/10 text-base font-black uppercase tracking-wider transition-all"
                      >
                        <Eye className="w-4 h-4" />
                        Details
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="px-6 py-16 text-center text-slate-500 text-xs font-black uppercase tracking-widest">
                    No API logs found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination controls */}
        <div className="px-6 py-4 border-t border-slate-800/80 bg-slate-950/40 flex items-center justify-between sticky bottom-0 z-10">
          <div className="text-sm font-black text-slate-500 uppercase tracking-widest whitespace-nowrap">
            Showing Page {currentPage}
          </div>
          <div className="flex gap-2">
            <button
              onClick={handlePrevPage}
              disabled={pagination.offset === 0 || loading}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-900 border border-slate-800 text-slate-500 hover:text-white hover:border-blue-500/50 hover:bg-blue-500/5 transition-all disabled:opacity-20 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={handleNextPage}
              disabled={logs.length < pagination.limit || loading}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-900 border border-slate-800 text-slate-500 hover:text-white hover:border-blue-500/50 hover:bg-blue-500/5 transition-all disabled:opacity-20 disabled:cursor-not-allowed"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Log Detail Modal */}
      {selectedLogId && (
        <LogDetailModal
          logId={selectedLogId}
          onClose={() => setSelectedLogId(null)}
        />
      )}
    </div>
  );
}
